import type { SectionSidebarNavLinkItem } from "@/components/SectionSidebarNav";

export interface DokumenteHubCard {
  key: string;
  title: string;
  description: string;
  href: string;
}

export interface DokumenteHubSection {
  key: string;
  title: string;
  subtitle: string;
  href: string;
  cards: DokumenteHubCard[];
}

/** Linke Sektionsnavigation unter Dokumente – nur für Büro. */
export const DOKUMENTE_SECTION_NAV_ITEMS: SectionSidebarNavLinkItem[] = [
  {
    key: "dokumente",
    label: "Dokumente",
    href: "/dokumente",
    end: true,
  },
  {
    key: "buchhaltung",
    label: "Buchhaltung",
    href: "/dokumente/buchhaltung",
    badgeKeys: ["mahnwesen-faellig"],
    children: [
      {
        key: "uebersicht",
        label: "Übersicht",
        href: "/dokumente/buchhaltung",
        end: true,
      },
      {
        key: "rechnungen",
        label: "Rechnungen",
        href: "/dokumente/rechnungen",
      },
      {
        key: "offene-posten",
        label: "Offene Posten",
        href: "/dokumente/offene-posten",
      },
      {
        key: "mahnungen",
        label: "Mahnungen",
        href: "/dokumente/mahnwesen",
        badgeKeys: ["mahnwesen-faellig"],
      },
      {
        key: "einstellungen",
        label: "Einstellungen",
        href: "/dokumente/einstellungen",
      },
    ],
  },
  {
    key: "nachweise",
    label: "Nachweise",
    href: "/dokumente/nachweise",
    badgeKeys: [
      "billing-unread",
      "billing-assigned",
      "billing-answered-unread",
      "project-completions-open",
      "lieferschein-billing-unread",
      "lieferschein-billing-questions",
    ],
    children: [
      {
        key: "stundennachweise",
        label: "Stundennachweise",
        href: "/dokumente/stundennachweise",
        badgeKeys: ["billing-unread", "billing-assigned", "billing-answered-unread"],
      },
      {
        key: "lieferscheine",
        label: "Lieferscheine",
        href: "/dokumente/lieferscheine",
        badgeKeys: ["lieferschein-billing-unread", "lieferschein-billing-questions"],
      },
      {
        key: "fertigstellungsmeldungen",
        label: "Fertigstellungsmeldungen",
        href: "/dokumente/fertigstellungsmeldungen",
        badgeKeys: ["project-completions-open"],
      },
    ],
  },
  {
    key: "angebotswesen",
    label: "Angebote",
    href: "/dokumente/angebotswesen",
    badgeKeys: ["angebote-open"],
    children: [
      {
        key: "angebotsanfragen",
        label: "Angebotsanfragen",
        href: "/dokumente/angebotsanfragen",
        badgeKeys: ["angebote-open"],
      },
      {
        key: "angebote",
        label: "Angebote",
        href: "/dokumente/angebote",
      },
    ],
  },
];

export const DOKUMENTE_HUB_SECTIONS: Record<string, DokumenteHubSection> = {
  buchhaltung: {
    key: "buchhaltung",
    title: "Buchhaltung",
    subtitle: "Rechnungsentwürfe, offene Posten, Versand und fällige Mahnungen auf einen Blick.",
    href: "/dokumente/buchhaltung",
    cards: [
      {
        key: "rechnungen",
        title: "Rechnungen",
        description: "Ausgangsrechnungen anlegen, prüfen und den Zahlungsstatus im Blick behalten.",
        href: "/dokumente/rechnungen",
      },
      {
        key: "offene-posten",
        title: "Offene Posten",
        description: "Offene Rechnungen mit Restsaldo, Fälligkeit, Altersstruktur und Summe.",
        href: "/dokumente/offene-posten",
      },
      {
        key: "mahnungen",
        title: "Mahnungen",
        description: "Überfällige Rechnungen mahnen und den Mahnstatus verfolgen.",
        href: "/dokumente/mahnwesen",
      },
      {
        key: "einstellungen",
        title: "Einstellungen",
        description: "Standardtexte, E-Mail-Vorlagen, Stundensätze und Layout für Dokumente.",
        href: "/dokumente/einstellungen",
      },
    ],
  },
  nachweise: {
    key: "nachweise",
    title: "Nachweise",
    subtitle: "Stundennachweise, Lieferscheine und Fertigstellungsmeldungen aus der Ausführung.",
    href: "/dokumente/nachweise",
    cards: [
      {
        key: "stundennachweise",
        title: "Stundennachweise",
        description: "Stunden prüfen, Rückfragen klären und Nachweise für die Abrechnung nutzen.",
        href: "/dokumente/stundennachweise",
      },
      {
        key: "lieferscheine",
        title: "Lieferscheine",
        description: "Gelieferte Positionen prüfen und offene Rückfragen zur Abrechnung klären.",
        href: "/dokumente/lieferscheine",
      },
      {
        key: "fertigstellungsmeldungen",
        title: "Fertigstellungsmeldungen",
        description: "Abgeschlossene Montagen sichten und den weiteren Ablauf anstoßen.",
        href: "/dokumente/fertigstellungsmeldungen",
      },
    ],
  },
  angebotswesen: {
    key: "angebotswesen",
    title: "Angebote",
    subtitle: "Anfragen aus Hero und erstellte Angebotsdokumente an einem Ort.",
    href: "/dokumente/angebotswesen",
    cards: [
      {
        key: "angebotsanfragen",
        title: "Angebotsanfragen",
        description: "Eingehende Anfragen aus Hero bearbeiten und in Angebote überführen.",
        href: "/dokumente/angebotsanfragen",
      },
      {
        key: "angebote",
        title: "Angebote",
        description: "Erstellte Angebote nachfassen, den Status pflegen und in Aufträge überführen.",
        href: "/dokumente/angebote",
      },
    ],
  },
};

/** Top-Nav-Badges für den Dokumente-Hub (Büro). */
export const DOKUMENTE_HUB_BADGE_KEYS = [
  "mahnwesen-faellig",
  "angebote-open",
  "billing-unread",
  "billing-assigned",
  "billing-answered-unread",
  "project-completions-open",
  "lieferschein-billing-unread",
  "lieferschein-billing-questions",
] as const;

function pathMatchesHref(pathname: string, href: string, end?: boolean): boolean {
  const path = (pathname || "").replace(/\/$/, "") || "/";
  const target = (href || "").replace(/\/$/, "") || "/";
  if (end) return path === target;
  return path === target || path.startsWith(`${target}/`);
}

function navLinkIsActive(item: SectionSidebarNavLinkItem, pathname: string): boolean {
  if (pathMatchesHref(pathname, item.href, item.end)) return true;
  return (item.children ?? []).some((child) => {
    if (!("href" in child)) return false;
    return navLinkIsActive(child, pathname);
  });
}

/** Parent-Einträge als aktiv markieren, wenn eine Unterseite geöffnet ist. */
export function applyDokumenteNavActive(
  items: SectionSidebarNavLinkItem[],
  pathname: string,
): SectionSidebarNavLinkItem[] {
  return items.map((item) => {
    const children = item.children
      ?.filter((child): child is SectionSidebarNavLinkItem => "href" in child)
      .map((child) => ({
        ...child,
        active: navLinkIsActive(child, pathname),
      }));
    return {
      ...item,
      active: navLinkIsActive(item, pathname),
      children,
    };
  });
}

/** Volle Editor-/Konfigurator-Pfade ohne linke Sektionsnavigation. */
export function isDokumenteFullscreenPath(pathname: string): boolean {
  const path = (pathname || "").replace(/\/$/, "") || "/";
  if (path === "/dokumente/neu" || path.startsWith("/dokumente/neu/")) return true;
  if (path === "/dokumente/layout" || path.startsWith("/dokumente/layout/")) return true;
  if (/^\/dokumente\/\d+(\/|$)/.test(path)) return true;
  return false;
}
